import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { SearchUser, UpdateUserBody, User } from "./types/users";

export const useUsersApi = () => {
  const api = useApi();

  const useAdminUsers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/users", query);
      return useSWR<Paginated<SearchUser>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchUser>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminUsers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) =>
      api.get<Paginated<SearchUser>>({ url: buildUrl("/admin/users", query), signal }).then((x) => x.items),
    [api],
  );

  const getAdminUser = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/users`;
      return useSWRMutation<User, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<User>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const updateUser = useCallback(
    async (itemId: string, body: UpdateUserBody): Promise<void> => {
      const url = `/admin/users/${itemId}`;
      await api.put<UpdateUserBody>(url, body);
      mutateCache(["/systems"], url);
    },
    [api],
  );

  return { useAdminUsers, getAdminUsers, getAdminUser, updateUser: updateUser };
};
